/**
 * StudioOutline - the studio's left navigation drawer. Lists the document's
 * logical sections with role badges; lets the admin select, drag-reorder, add,
 * and (via a row menu) duplicate / move / delete. Navigation only - editing
 * happens on the canvas + inspector.
 */

import { Button, DropdownMenu, Pill, Tooltip } from '@protoss/ui';
import { DotsVerticalIcon, PlusIcon } from '@protoss/ui/icons';
import * as React from 'react';
import {
  CONTENT_TYPE_META,
  SELECTABLE_CONTENT_TYPES,
  SHORT_CONTENT_TYPE_LABEL,
  sectionLabel,
  type LogicalContentType,
  type LogicalSection,
} from '../../lib/sowContract';

export interface StudioOutlineProps {
  sections: LogicalSection[];
  selectedId: string | null;
  canEdit: boolean;
  onSelect: (id: string) => void;
  onReorder: (id: string, toIndex: number) => void;
  onMove: (id: string, dir: 'up' | 'down') => void;
  onDuplicate: (id: string) => void;
  onRemove: (id: string) => void;
  onAdd: (type: LogicalContentType) => void;
}

export function StudioOutline({
  sections,
  selectedId,
  canEdit,
  onSelect,
  onReorder,
  onMove,
  onDuplicate,
  onRemove,
  onAdd,
}: StudioOutlineProps) {
  const [dragId, setDragId] = React.useState<string | null>(null);
  const [overIndex, setOverIndex] = React.useState<number | null>(null);

  return (
    <div className="sow-studio-outline">
      <div className="sow-studio-outline__header">
        <span className="sow-studio-outline__title">Outline</span>
        {canEdit ? (
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <Button type="button" variant="ghost" size="sm" aria-label="Add section">
                <PlusIcon />
              </Button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Content align="end" className="dropdown-content">
              {SELECTABLE_CONTENT_TYPES.map((type) => (
                <DropdownMenu.Item
                  key={type}
                  className="dropdown-item"
                  onSelect={() => onAdd(type)}
                >
                  {CONTENT_TYPE_META[type].label}
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Root>
        ) : null}
      </div>

      <ul className="sow-studio-outline__list" role="list">
        {sections.map((section, index) => {
          const meta = CONTENT_TYPE_META[section.contentType];
          const selected = section.id === selectedId;
          return (
            <li
              key={section.id}
              className={[
                'sow-studio-outline__row',
                selected ? 'sow-studio-outline__row--selected' : '',
                overIndex === index && dragId ? 'sow-studio-outline__row--drop' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onDragOver={(e) => {
                if (!dragId) return;
                e.preventDefault();
                setOverIndex(index);
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (dragId && dragId !== section.id) onReorder(dragId, index);
                setDragId(null);
                setOverIndex(null);
              }}
            >
              {canEdit ? (
                <Tooltip.Simple label="Drag to reorder" side="right">
                <span
                  className="sow-studio-outline__handle"
                  draggable
                  aria-hidden
                  onDragStart={() => setDragId(section.id)}
                  onDragEnd={() => {
                    setDragId(null);
                    setOverIndex(null);
                  }}
                >
                  ⠿
                </span>
              </Tooltip.Simple>
              ) : null}
              <button
                type="button"
                className="sow-studio-outline__select"
                aria-current={selected ? 'true' : undefined}
                onClick={() => onSelect(section.id)}
              >
                <span className="sow-studio-outline__label">{sectionLabel(section)}</span>
                <Pill variant={meta.tone}>{SHORT_CONTENT_TYPE_LABEL[section.contentType]}</Pill>
              </button>
              {canEdit && !section.isPreamble ? (
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger asChild>
                    <button
                      type="button"
                      className="sow-studio-icon-btn"
                      aria-label={`Actions for ${sectionLabel(section)}`}
                    >
                      <DotsVerticalIcon />
                    </button>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Content align="end" className="dropdown-content">
                    <DropdownMenu.Item
                      className="dropdown-item"
                      onSelect={() => onMove(section.id, 'up')}
                    >
                      Move up
                    </DropdownMenu.Item>
                    <DropdownMenu.Item
                      className="dropdown-item"
                      onSelect={() => onMove(section.id, 'down')}
                    >
                      Move down
                    </DropdownMenu.Item>
                    <DropdownMenu.Item
                      className="dropdown-item"
                      onSelect={() => onDuplicate(section.id)}
                    >
                      Duplicate
                    </DropdownMenu.Item>
                    <DropdownMenu.Item
                      className="dropdown-item dropdown-item--danger"
                      onSelect={() => {
                        // eslint-disable-next-line no-alert
                        if (window.confirm(`Delete "${sectionLabel(section)}"?`)) onRemove(section.id);
                      }}
                    >
                      Delete
                    </DropdownMenu.Item>
                  </DropdownMenu.Content>
                </DropdownMenu.Root>
              ) : null}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
